@use 'variables' as *;
@use 'mixins' as *;
@use 'src/components/cat-form-hint/cat-form-hint';
@use 'utils/color';
@use '_snippets/form-label';

:host {
  display: flex;
  @include cat-body('m', null);
}

:host([hidden]) {
  display: none;
}

.input-field,
.input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1 1 auto;
}

.input-field.input-horizontal {
  flex-direction: row;
  gap: 1rem;
}

.input-wrapper {
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  padding: 0 0.75rem;
  height: form-label.$cat-input-height;
  overflow: hidden;
  background: cat-token('color.ui.background.input');
  border-radius: cat-border-radius('m');
  box-shadow: inset 0 0 0 1px rgb(var(--border-color));
  transition: box-shadow cat-token('time.transition.s') linear;
  --border-color: #{cat-token('color.ui.border.dark', $wrap: false)};

  &.input-round {
    border-radius: 10rem;
  }

  &.input-readonly {
    pointer-events: none;
  }

  &.input-disabled {
    background: cat-token('color.ui.background.muted');
    cursor: not-allowed;
    color: cat-token('color.ui.font.muted');
  }

  &:not(.input-disabled):hover {
    box-shadow:
      inset 0 0 0 1px rgb(var(--border-color)),
      0 0 0 1px rgb(var(--border-color));
  }

  &:focus-within {
    outline: 2px solid cat-token('color.ui.border.focus');
    outline-offset: -1px;

    &:has(.clearable:focus) {
      outline: none;
    }
  }

  &.input-invalid {
    --border-color: #{cat-token('color.theme.danger.bg', 0.2, $wrap: false)};
  }

  &:has(input[type='file']) {
    padding-left: 0;
  }

  /* stylelint-disable property-no-vendor-prefix */
  &:has(input:-webkit-autofill) {
    &,
    &:hover,
    &:focus {
      background-color: cat-token('color.ui.background.inputAutofill');
    }
  }
  /* stylelint-enable property-no-vendor-prefix */
}

.text-prefix,
.text-suffix {
  display: inline-flex;
  align-items: center;
  @include cat-select(none);
  color: cat-token('color.ui.font.muted');
}

.text-prefix {
  border-right: 1px solid cat-token('color.ui.border.dark');
  padding-right: 0.75rem;
}

.text-suffix {
  border-left: 1px solid cat-token('color.ui.border.dark');
  padding-left: 0.75rem;
}

.icon-loading,
.icon-prefix,
.icon-suffix {
  align-self: center;
}

.input-outer-wrapper {
  display: flex;
}

.input-inner-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  flex: 1 1 auto;
}

input {
  font: inherit;
  margin: 0;
  padding: 0;
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: none;
  @include cat-ellipsis;

  .input-disabled & {
    cursor: not-allowed;
    color: cat-token('color.ui.font.muted');
  }

  &.has-clearable,
  &.has-toggle-password {
    padding-right: 1.5rem;
  }

  &.has-clearable.has-toggle-password {
    padding-right: 3.5rem;
  }

  &::placeholder {
    color: cat-token('color.ui.font.muted');
    opacity: 1;
  }

  &::file-selector-button {
    background-color: transparent;
    cursor: pointer;
    border: 1px solid var(--cat-border-color-dark, #ccc);
    border-radius: cat-border-radius('m');
    height: 1.75rem;
    margin: 0 0.75rem 0 0.375rem;
  }

  /* stylelint-disable property-no-vendor-prefix */
  &:-webkit-autofill {
    &,
    &:hover,
    &:focus {
      -webkit-box-shadow: 0 0 0 9999px cat-token('color.ui.background.inputAutofill') inset;
    }
  }
  /* stylelint-enable property-no-vendor-prefix */
}

.clearable {
  position: absolute;
  top: calc(50% - 1rem);
  right: -0.5rem;
}

.toggle-password {
  position: absolute;
  top: calc(50% - 1rem);
  right: -0.5rem;

  .has-clearable ~ & {
    right: 1.5rem;
  }
}

/* Color picker */
.input-inner-wrapper.has-clearable,
.input-inner-wrapper.has-toggle-password {
  padding-right: 2rem;
}

.input-inner-wrapper.has-clearable.has-toggle-password {
  padding-right: 4rem;
}

input[type='color'] {
  flex: 0 0 1.5625rem;
  width: 1.5625rem;
  height: 1.5625rem;
  border: 1px solid var(--cat-border-color-dark, #ccc);
  border-radius: 0.25rem;
  background: none;
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  padding: 0 !important;
}

/* Chrome / Safari */
input[type='color']::-webkit-color-swatch-wrapper {
  padding: 0;
}

input[type='color']::-webkit-color-swatch {
  border: none;
  border-radius: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

/* Firefox */
input[type='color']::-moz-color-swatch {
  border: none;
  border-radius: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.color-value {
  flex-grow: 1;
}

// ----- date- & timepicker

:host(.cat-date-input),
:host(.cat-time-input) {
  .input-wrapper {
    z-index: 1;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}
